import {createSlice, createAsyncThunk, createSelector} from '@reduxjs/toolkit';
import { HOSTNAME } from '../../configs/Domain';


const fetchListFromServer = async (endpoint, abortController) => {
  const signal = abortController.signal;

  try {
    const response = await fetch(`${HOSTNAME}${endpoint}`, { signal });
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

export const loadList = createAsyncThunk(
  'list/load',
  async ({ endpoint, cacheKey }, { getState, rejectWithValue }) => {
    const cache = getState().global.cache;
    const status = getState().global.status[cacheKey];

    // Don't fetch if we already have data and aren't in an error state
    if (cache[cacheKey]?.length > 0 && status !== 'failed') {
      return { cacheKey, data: cache[cacheKey] };
    }

    const abortController = new AbortController();

    try {
      const data = await fetchListFromServer(endpoint, abortController);
      return { cacheKey, data };
    } catch (error) {
      return rejectWithValue({ cacheKey, error: error.message });
    }
  },
  {
    condition: ({ cacheKey }, { getState }) => {
      const status = getState().global.status[cacheKey];
      // Prevent multiple requests while loading
      return status !== 'loading';
    },
  }
);




const globalResourcesSlice = createSlice({
  name: 'global',
  initialState: {
    cache: {
      tickerSearchList: null,
      sectorsIndustries: null,
    },
    status: {
      tickerSearchList: 'idle',
      sectorsIndustries: 'idle',
      // Add more lists here as needed
    },
    error: {
      tickerSearchList: null,
      sectorsIndustries: null,
      // Add more lists here as needed
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadList.pending, (state, action) => {
        state.status[action.meta.arg.cacheKey] = 'loading';
      })
      .addCase(loadList.fulfilled, (state, action) => {
        return {
          ...state,
          status: {
            ...state.status,
            [action.payload.cacheKey]: 'succeeded',
          },
          cache: {
            ...state.cache,
            [action.payload.cacheKey]: action.payload.data,
          },
          error: {
            ...state.error,
            [action.payload.cacheKey]: null,
          },
        };
      })
      .addCase(loadList.rejected, (state, action) => {
        return {
          ...state,
          status: {
            ...state.status,
            [action.meta.arg.cacheKey]: 'failed',
          },
          error: {
            ...state.error,
            [action.meta.arg.cacheKey]: action.payload,
          },
        };
      });
  },
});

export default globalResourcesSlice.reducer;


export const loadTickerSearchList = () => loadList({ endpoint: '/tickers/catalog', cacheKey: 'tickerSearchList' });
export const loadSectorsIndustries = () => loadList({ endpoint: '/tickers/categories/list', cacheKey: 'sectorsIndustries' });


export const selectTickerSearchList = (state) => state.global.cache['tickerSearchList'] || [];


export const getTickersBySymbols = createSelector(
  [(state) => state.global.cache['tickerSearchList'] || [], (state, symbols) => symbols],
  (list, symbols) => {
    const symbolSet = new Set(symbols); // Efficient lookup
    const filteredTickers = list.filter(ticker => symbolSet.has(ticker.symbol));
    return filteredTickers;
  }
);

// Selector to get metadata for a specific ticker symbol
export const selectTickerMetadata = (state, symbol) => {
  const list = state.global.cache['tickerSearchList'] || [];
  return list.find(ticker => ticker.symbol === symbol);
};

